import {
  addDoc,
  collection,
  DocumentData,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  serverTimestamp,
  Unsubscribe,
  where,
} from 'firebase/firestore';
import { db } from '../../../shared/utils/firebase';
import { whereLocation } from '../../auth/managedLocations';
import { toDate } from '../../../shared/utils/dateHelpers';
import { createNotification } from '../../../shared/utils/notifications';
import { summarizeSuccessRecord } from '../../../shared/success/successRecords';
import {
  ClientSuccessHistoryEntry,
  NominationScope,
  NominationTrainingSnapshot,
  SubmitNominationInput,
  SubmitNominationResult,
  SuccessNominationRecord,
} from '../nomination.types';
import { excerpt, normalizeClientName } from '../utils/nominationFilters';
import { validateNominationFields } from '../utils/nominationValidation';

export const NOMINATIONS_COLLECTION = 'client_success_nominations';
const CLIENT_SUCCESS_COLLECTION = 'client_success';
const LOG_PREFIX = '[success-nominations]';
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 500;
/** Upper bound for the realtime review feed; nominations are low volume. */
const REVIEW_FEED_LIMIT = 500;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

async function withRetry<T>(operationName: string, operation: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      console.error(`${LOG_PREFIX} ${operationName} attempt ${attempt}/${MAX_RETRIES} failed`, error);
      if (attempt < MAX_RETRIES) {
        await sleep(RETRY_BASE_DELAY_MS * attempt);
      }
    }
  }
  throw lastError;
}

const toOptionalString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized.length ? normalized : null;
};

const toCount = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const mapTraining = (raw: unknown): NominationTrainingSnapshot => {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    sessionsCompleted: toCount(data.sessionsCompleted),
    totalVisits: toCount(data.totalVisits),
    noShows: toCount(data.noShows),
    lateCancels: toCount(data.lateCancels),
    firstSessionDate: toOptionalString(data.firstSessionDate),
    latestSessionDate: toOptionalString(data.latestSessionDate),
  };
};

export function mapNominationDoc(snapshot: QueryDocumentSnapshot<DocumentData>): SuccessNominationRecord {
  const data = snapshot.data();
  const nominator = (data.nominated_by && typeof data.nominated_by === 'object' ? data.nominated_by : {}) as Record<string, unknown>;
  const createdAt = toDate(data.created_at) || new Date();
  const updatedAt = toDate(data.updated_at) || createdAt;
  const nominatedByUid = toOptionalString(data.nominated_by_uid) || toOptionalString(nominator.uid) || '';
  const nominatedByName = toOptionalString(data.nominated_by_name) || toOptionalString(nominator.name) || 'Unknown staff';
  const role = nominator.role === 'ADMIN' || nominator.role === 'MANAGER' ? nominator.role : 'USER';

  return {
    id: snapshot.id,
    client_id: toOptionalString(data.client_id) || snapshot.id,
    client_name: toOptionalString(data.client_name) || 'Unknown client',
    client_location: toOptionalString(data.client_location) || '',
    lead_coach_id: toOptionalString(data.lead_coach_id),
    lead_coach_name: toOptionalString(data.lead_coach_name),
    current_programme: toOptionalString(data.current_programme),
    wins: typeof data.wins === 'string' ? data.wins : '',
    comments: typeof data.comments === 'string' ? data.comments : '',
    reason: typeof data.reason === 'string' ? data.reason : '',
    training: mapTraining(data.training),
    nominated_by: {
      uid: nominatedByUid,
      name: nominatedByName,
      email: toOptionalString(nominator.email),
      role,
      coachId: toOptionalString(nominator.coachId),
    },
    nominated_by_uid: nominatedByUid,
    nominated_by_name: nominatedByName,
    status: 'submitted',
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/**
 * Builds the role-scoped constraints for nomination queries. The shapes here
 * must stay aligned with `firestore.rules` (`client_success_nominations`):
 *  - ADMIN: unrestricted
 *  - MANAGER: `client_location in managedLocations()`
 *  - USER: `nominated_by_uid == uid`
 */
export function buildScopeConstraints(scope: NominationScope): QueryConstraint[] | null {
  if (scope.role === 'ADMIN') return [];
  if (scope.role === 'MANAGER') {
    if (scope.assignedLocations.length === 0) return null;
    return [whereLocation('client_location', scope.assignedLocations)];
  }
  if (!scope.uid) return null;
  return [where('nominated_by_uid', '==', scope.uid)];
}

/** Realtime feed for the Success Nominations review page. */
export function subscribeToNominations(
  scope: NominationScope,
  onData: (records: SuccessNominationRecord[]) => void,
  onError: (error: Error) => void
): Unsubscribe {
  const scopeConstraints = buildScopeConstraints(scope);
  if (!scopeConstraints) {
    onError(new Error('Your account is missing the location or identity needed to view nominations.'));
    return () => undefined;
  }

  const nominationsQuery = query(
    collection(db, NOMINATIONS_COLLECTION),
    ...scopeConstraints,
    orderBy('created_at', 'desc'),
    limit(REVIEW_FEED_LIMIT)
  );

  console.info(`${LOG_PREFIX} subscribing`, { role: scope.role, locations: scope.assignedLocations });

  return onSnapshot(
    nominationsQuery,
    snapshot => {
      const records = snapshot.docs.map(mapNominationDoc);
      console.info(`${LOG_PREFIX} snapshot received`, { count: records.length });
      onData(records);
    },
    error => {
      console.error(`${LOG_PREFIX} subscription failed`, error);
      onError(error);
    }
  );
}

/** Nominations for one client, scoped so the query satisfies security rules for every role. */
export async function fetchClientNominations(
  clientId: string,
  scope: NominationScope
): Promise<SuccessNominationRecord[]> {
  const scopeConstraints = buildScopeConstraints(scope);
  if (!scopeConstraints || !clientId) return [];

  const nominationsQuery = query(
    collection(db, NOMINATIONS_COLLECTION),
    where('client_id', '==', clientId),
    ...scopeConstraints,
    orderBy('created_at', 'desc')
  );

  const snapshot = await withRetry('fetch_client_nominations', () => getDocs(nominationsQuery));
  return snapshot.docs.map(mapNominationDoc);
}

/** Previous `client_success` entries for a client, newest first (used by the review history dialog). */
export async function fetchClientSuccessHistory(clientId: string): Promise<ClientSuccessHistoryEntry[]> {
  if (!clientId) return [];

  const historyQuery = query(
    collection(db, CLIENT_SUCCESS_COLLECTION),
    where('client_id', '==', clientId),
    orderBy('created_at', 'desc')
  );

  const snapshot = await withRetry('fetch_client_success_history', () => getDocs(historyQuery));
  return snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    const summary = summarizeSuccessRecord(docSnap.id, data);
    return {
      id: docSnap.id,
      kind: summary.kind,
      activityAt: summary.activityAt,
      goal: typeof data.goal === 'string' ? data.goal : '',
      goal_update: typeof data.goal_update === 'string' ? data.goal_update : '',
      goal_achieved: data.goal_achieved === true,
      targets: [1, 2, 3].map(index => ({
        label: `Target ${index}`,
        value: typeof data[`target${index}`] === 'string' ? data[`target${index}`] : '',
        update: typeof data[`target${index}_update`] === 'string' ? data[`target${index}_update`] : '',
        achieved: data[`target${index}_achieved`] === true,
      })),
      comments: typeof data.comments === 'string' ? data.comments : '',
      created_by: typeof data.created_by === 'string' ? data.created_by : '',
      created_at: toDate(data.created_at),
      updated_at: toDate(data.updated_at),
    };
  });
}

/**
 * Persists a nomination and notifies the client's location manager (and admins).
 * The notification is best-effort: `createNotification` never throws, so a
 * notification failure never loses the nomination itself; the result reports
 * whether it was delivered so the UI can warn the nominator.
 */
export async function submitNomination(input: SubmitNominationInput): Promise<SubmitNominationResult> {
  const wins = input.wins.trim();
  const comments = input.comments.trim();
  const reason = input.reason.trim();

  const validationErrors = validateNominationFields({ wins, comments, reason });
  if (Object.keys(validationErrors).length > 0) {
    throw new Error(Object.values(validationErrors)[0]);
  }
  if (!input.nominator.uid) {
    throw new Error('You need to be signed in to submit a nomination.');
  }
  if (!input.client.client_id) {
    throw new Error('This client is missing an ID, so the nomination cannot be saved.');
  }

  const clientLocation = input.client.primary_location?.trim() || '';
  const payload = {
    client_id: String(input.client.client_id),
    client_name: input.client.client_name || 'Unknown client',
    client_location: clientLocation,
    lead_coach_id: toOptionalString(input.client.lead_coach_id),
    lead_coach_name: toOptionalString(input.client.lead_coach_name),
    current_programme: toOptionalString(input.client.current_programme),
    wins,
    comments,
    reason,
    training: input.training,
    nominated_by: {
      uid: input.nominator.uid,
      name: input.nominator.name,
      email: input.nominator.email,
      role: input.nominator.role,
      coachId: input.nominator.coachId,
    },
    nominated_by_uid: input.nominator.uid,
    nominated_by_name: input.nominator.name,
    status: 'submitted' as const,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };

  console.info(`${LOG_PREFIX} submitting nomination`, {
    clientId: payload.client_id,
    clientLocation,
    nominatedBy: input.nominator.uid,
    sessionsCompleted: input.training.sessionsCompleted,
  });

  const docRef = await withRetry('create_nomination', () => addDoc(collection(db, NOMINATIONS_COLLECTION), payload));

  const displayName = normalizeClientName(payload.client_name) || payload.client_name;
  const notificationSent = await createNotification({
    type: 'success_nomination',
    clientId: payload.client_id,
    clientName: payload.client_name,
    message: `${input.nominator.name} nominated ${displayName} for Client Success`,
    metadata: {
      submittedBy: input.nominator.uid,
      submittedByName: input.nominator.name,
      nominationId: docRef.id,
      sessionsCompleted: input.training.sessionsCompleted,
      clientLocation,
      excerpt: excerpt(reason, 140),
    },
    recipients: {
      roles: ['MANAGER', 'ADMIN'],
      locations: clientLocation ? [clientLocation] : [],
    },
    managerLocations: clientLocation ? [clientLocation] : [],
    submittedBy: {
      uid: input.nominator.uid,
      name: input.nominator.name,
      role: input.nominator.role,
      coachId: input.nominator.coachId || undefined,
    },
    eventKey: docRef.id,
  });

  if (!notificationSent) {
    console.warn(`${LOG_PREFIX} nomination saved but the manager notification was not written`, {
      nominationId: docRef.id,
      clientLocation,
    });
  }

  console.info(`${LOG_PREFIX} nomination submitted`, { nominationId: docRef.id, notificationSent });
  return { nominationId: docRef.id, notificationSent };
}
